import { Form, Input, InputNumber, notification } from 'antd';
import { BaseButton } from 'components/button/button-base';
import { Clinic } from 'lib/documents/clinic';
import { useCreateClinic } from 'lib/hooks/tanstack/useCreateClinic';
import { useInviteClinicAdmin } from 'lib/hooks/tanstack/useInviteClinicAdmin';
import { useErrorList } from 'lib/utils/useErrorList';
import React from 'react';
import { FormQuestion } from 'lib/types/form';

const clinicDetailsQuestions: FormQuestion[] = [
  {
    name: 'name',
    label: 'Organisation name',
    component: () => <Input />,
    rules: [{ required: true, message: 'Please input the organisation name' }],
    extra: 'The official name of your organisation'
  },
  {
    name: 'contactEmail',
    label: 'Organisation contact email',
    component: () => <Input />,
    rules: [
      {
        required: true,
        message: 'Please input the organisation contact email'
      },
      { type: 'email', message: 'The input is not valid E-mail' }
    ],
    extra: 'The main email address for organisation communications'
  },
  {
    name: 'contactPhoneNumber',
    label: 'Organisation contact phone number',
    component: () => <Input />,
    rules: [
      {
        required: true,
        message: 'Please input the organisation contact phone number'
      }
    ],
    extra: 'The primary phone number for organisation inquiries'
  },
  {
    name: ['billing', 'address'],
    label: 'Organisation billing address',
    component: () => <Input />,
    rules: [{ required: true, message: 'Please input the billing address' }],
    extra: 'The address used for billing and invoicing'
  },
  {
    name: ['billing', 'primaryContact', 'name'],
    label: 'Organisation billing primary contact name',
    component: () => <Input />,
    rules: [
      { required: true, message: 'Please input the primary contact name' }
    ],
    extra: 'The name of the primary contact for billing matters'
  },
  {
    name: ['billing', 'primaryContact', 'email'],
    label: 'Billing primary contact email',
    component: () => <Input />,
    rules: [
      { required: true, message: 'Please input the primary contact email' },
      { type: 'email', message: 'The input is not valid E-mail' }
    ],
    extra: 'The email address of the primary contact for billing'
  },
  {
    name: 'numberOfUsers',
    label: 'Number of users',
    component: () => <InputNumber min={1} style={{ width: '100%' }} />,
    rules: [{ required: true, message: 'Please input the number of users' }],
    extra: 'How many users will need access to the system'
  },
  {
    name: 'estimatedNumberOfTestTakers',
    label: 'Estimated number of test-takers per year',
    component: () => <InputNumber min={0} style={{ width: '100%' }} />,
    rules: [
      {
        required: true,
        message: 'Please input the estimated number of test-takers'
      }
    ],
    extra: 'Approximate number of patients taking assessments annually'
  }
];

const clinicAdminQuestions: FormQuestion[] = [
  {
    name: ['clinicAdmin', 'firstName'],
    label: 'Clinic admin first name',
    component: () => <Input />,
    rules: [
      { required: true, message: 'Please input the ClinicAdmin first name' }
    ],
    extra: 'The first name of the clinic administrator'
  },
  {
    name: ['clinicAdmin', 'lastName'],
    label: 'Clinic admin last name',
    component: () => <Input />,
    rules: [
      { required: true, message: 'Please input the ClinicAdmin last name' }
    ],
    extra: 'The last name of the clinic administrator'
  },
  {
    name: ['clinicAdmin', 'email'],
    label: 'Clinic admin email',
    component: () => <Input />,
    rules: [
      { required: true, message: 'Please input the ClinicAdmin email' },
      { type: 'email', message: 'The input is not valid E-mail' }
    ],
    extra: 'The email address of the clinic administrator'
  }
];

interface AddOrganizationDrawerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

interface FormValues {
  name: string;
  contactEmail: string;
  contactPhoneNumber: string;
  billing: {
    address: string;
    primaryContact: {
      name: string;
    };
  };
  numberOfUser: number;
  estimatedNumberOfTestTakers: number;
  clinicAdmin: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

export const AddOrganizationDrawer: React.FC<AddOrganizationDrawerProps> = ({
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm<FormValues>();
  const createClinicMutation = useCreateClinic();
  const inviteClinicAdminMutation = useInviteClinicAdmin();
  const errorList = useErrorList();

  async function onFinish(values: any) {
    try {
      // 1. Create Clinic
      // Construct the Clinic object based on the API spec
      const clinicPayload: Omit<Clinic, 'id' | 'metadata'> = {
        name: values.name,
        country: values.country || 'canada', // Assuming a default or that these will be added to the form later
        address: values.billing.address,
        phoneNumber: values.contactPhoneNumber,
        faxNumber: values.faxNumber || 'N/A',
        paymentConfig: {
          assessment: { amount: 0, currency: 'CAD', enabled: true }
        }, // Default payment config
        crx_settings: { FEATURE_FLAGS: { ASSESSMENT_BASELINE: true } } // Default settings
      };

      const createClinicResponse = await createClinicMutation.mutateAsync(
        clinicPayload
      );
      const clinicId = createClinicResponse.data.id;

      // 2. Invite Clinic Admin
      const clinicAdminPayload = {
        clinicId: clinicId,
        firstName: values.clinicAdmin.firstName,
        lastName: values.clinicAdmin.lastName,
        email: values.clinicAdmin.email
      };

      await inviteClinicAdminMutation.mutateAsync(clinicAdminPayload);

      notification.success({
        message: 'Success',
        description: 'Organization created and admin invited successfully'
      });
      onSuccess(); // Close modal and refresh table
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description:
          error.message || 'Failed to create organization or invite admin.'
      });
      console.error('Submission error:', error);
      errorList.addError(error);
    }
  }

  const isSubmitting =
    createClinicMutation.isPending || inviteClinicAdminMutation.isPending;

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <h2>Organization Details</h2>
      {clinicDetailsQuestions.map(question => (
        <Form.Item
          key={JSON.stringify(question.name)}
          name={question.name}
          label={question.label}
          rules={question.rules}
          extra={question.extra}
        >
          {question.component()}
        </Form.Item>
      ))}

      <h2>Clinic Admin Details</h2>
      {clinicAdminQuestions.map(question => (
        <Form.Item
          key={JSON.stringify(question.name)}
          name={question.name}
          label={question.label}
          rules={question.rules}
          extra={question.extra}
        >
          {question.component()}
        </Form.Item>
      ))}

      <Form.Item>
        <BaseButton type="primary" htmlType="submit" loading={isSubmitting}>
          Create Organization and Invite Admin
        </BaseButton>
        <BaseButton
          type="text"
          onClick={onCancel}
          style={{ marginLeft: 8 }}
          disabled={isSubmitting}
        >
          Cancel
        </BaseButton>
      </Form.Item>
    </Form>
  );
};
